import { useLogError } from '@/src/common/hooks/useLogError'
import { router, useLocalSearchParams } from 'expo-router'
import { Platform, TouchableOpacity, View } from 'react-native'
import { Ionicons } from '@expo/vector-icons'
import * as FileSystem from 'expo-file-system'
import * as Sharing from 'expo-sharing'
import { formatDate, formatDateToLongString } from '@/src/common/utils/date'
import {
  useCardManagementStackScreensAreModals,
  useCardStackScreen,
} from '@/src/app/main/(tabs)/card/card-management/_common/cardStackScreen'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import type { Api } from '@/src/store/api'
import { z } from 'zod/v4'
import { ID } from '@acre-rewards/lib'

const StatementScreenParams = z.object({
  id: z.union([ID.schema.Statement, z.string().brand('highnote statement id')]),
  statementDate: z.string().optional(),
})

export function useStatementScreenParams() {
  return StatementScreenParams.safeParse(useLocalSearchParams()).data
}

export type StatementScreenParams = NonNullable<ReturnType<typeof useStatementScreenParams>>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createUseStatementScreenProps = <T extends any[]>(
  useGetStatementPdfUrl: (
    statementScreenParams?: StatementScreenParams,
    ...args: T
  ) =>
    | {
        loading?: boolean
        error?: unknown
        pdfLink?: (typeof Api.endpoints)['GET /card/:cardAccountId/statements/:statementId/pdf-link']['Types']['ResultType']
      }
    | undefined,
) =>
  function useStatementScreenProps(
    ...[statementScreenParams, ...args]: Parameters<typeof useGetStatementPdfUrl>
  ) {
    const logError = useLogError()
    const statementName = `Mesa Homeowners Card Statement${statementScreenParams?.statementDate ? ` ${formatDate(new Date(statementScreenParams.statementDate))}` : ''}.pdf`
    const { pdfLink, loading, error } = useGetStatementPdfUrl(statementScreenParams, ...args) || {}

    async function downloadStatement() {
      if (!pdfLink) {
        logError('Card Statement URL is not available')
        return
      }

      try {
        await downloadStatementFile(pdfLink, statementName)
      } catch (error) {
        logError('Card Statement Download failed', error)
      }
    }

    const cardManagementStackScreensAreModals = useCardManagementStackScreensAreModals()
    return {
      ...useCardStackScreen({
        name: '[id]',
        options: {
          title: 'Statement',
          ...(cardManagementStackScreensAreModals
            ? { presentation: 'transparentModal' }
            : undefined),
          headerShown: false,
        },
        sectionProps: {
          onBackPress: () => router.dismissTo('/main/card/card-management/statements'),
          closeStyle: {
            position: 'relative',
            right: 0,
          },
          right(close) {
            return (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 16,
                  position: 'absolute',
                  right: 16,
                }}
              >
                {Platform.OS !== 'web' && (
                  <TouchableOpacity onPress={() => void downloadStatement()}>
                    <Ionicons name="download-outline" size={24} color="white" />
                  </TouchableOpacity>
                )}
                {close}
              </View>
            )
          },
        },
        renderTitleInHeader() {
          return statementScreenParams?.statementDate
            ? formatDateToLongString(new Date(statementScreenParams.statementDate))
            : 'Statement'
        },
      }),
      pdfLink,
      loading,
      error,
    } as const
  }

const downloadStatementFile = (
  Platform.OS === 'web'
    ? async (url, statementName) => {
        try {
          const response = await fetch(url, { mode: 'no-cors' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(await response.blob())
          link.download = statementName
          link.click()
        } catch {
          await openInAppBrowser(url, statementName, 'tab')
        }
      }
    : async (url, statementName) => {
        if (!FileSystem.documentDirectory) {
          throw new Error('Document directory is not available when downloading statement')
        }

        const downloadResumable = FileSystem.createDownloadResumable(
          url,
          FileSystem.documentDirectory + statementName,
          {},
        )

        const result = await downloadResumable.downloadAsync()

        if (result?.uri) {
          await Sharing.shareAsync(result.uri)
        }
      }
) satisfies (url: string, statementName: string) => Promise<void>
